import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
    alpha,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    FormHelperText,
    Radio,
    RadioGroup,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import ThemeToggle from "../../components/ThemeToggle";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Popup from "../../components/Popup";
import { FEEDBACK, SUCCESS_NAVIGATE_DELAY_MS } from "../../utils/feedbackMessages";
import imgSchoolBuilding from "../../assets/landing/school-building.png";
import { BOARDING_MODES } from "../../utils/boardingUtils";
import { APP_NAME } from "../../config/brand";
import {
    SUBSCRIPTION_PLANS,
    TRIAL_DAYS,
    formatPriceMwk,
    getPlanById,
    PLAN_INCLUDES,
} from "../../config/subscriptionPlans";
import RegistrationPaymentChoiceModal from "../../components/subscription/RegistrationPaymentChoiceModal";
import { authLogout } from "../../redux/userRelated/userSlice";
import {
    createAdminAccount,
    clearRegistrationCheckout,
    isAvailabilityMessage,
    loadRegistrationCheckout,
} from "../../utils/adminRegistration";
import { notifySystemAdminOfSchoolRegistration } from "../../utils/schoolRegistrationEmail";
import { PASSWORD_MIN_HELPER, validateNewPassword } from "../../utils/passwordValidation";

const SCHOOL_TYPES = [
    { value: "Primary", label: "Primary", hint: "Standards 1–8, PSLCE" },
    { value: "Secondary", label: "Secondary", hint: "Forms 1–4, JCE & MSCE" },
    { value: "University", label: "University", hint: "Programmes & GPA" },
    { value: "Combined", label: "Combined", hint: "Primary & secondary sections" },
];

const BENEFITS = [
    "Director dashboard with school-wide KPIs",
    "Students, teachers, classes & subjects",
    "Fees, expenses & finance reports",
    "MIE-aligned scheme of work approvals",
    "Exams & attendance review workflows",
    "Hostel management for boarding schools",
];

const AdminRegisterPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialPlan = getPlanById(searchParams.get("plan")).id;

    const [toggle, setToggle] = useState(false);
    const [loader, setLoader] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [popupSeverity, setPopupSeverity] = useState(undefined);

    const [adminName, setAdminName] = useState("");
    const [schoolName, setSchoolName] = useState("");
    const [schoolType, setSchoolType] = useState("");
    const [boardingMode, setBoardingMode] = useState("DayOnly");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [subscriptionPlan, setSubscriptionPlan] = useState(initialPlan);

    const [adminNameError, setAdminNameError] = useState(false);
    const [schoolNameError, setSchoolNameError] = useState(false);
    const [schoolTypeError, setSchoolTypeError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorText, setPasswordErrorText] = useState("");

    const [showRegistrationPayment, setShowRegistrationPayment] = useState(false);
    const [pendingRegistration, setPendingRegistration] = useState(null);
    const [existingAccountError, setExistingAccountError] = useState("");
    const [resumeCheckout, setResumeCheckout] = useState(null);

    const role = "Admin";

    useEffect(() => {
        const saved = loadRegistrationCheckout();
        if (saved?.adminUser && saved?.pendingRegistration) {
            setPendingRegistration(saved.pendingRegistration);
            setResumeCheckout(saved);
            setShowRegistrationPayment(true);
            return;
        }
        dispatch(authLogout());
    }, [dispatch]);

    const buildRegistrationFields = () => ({
        name: adminName.trim(),
        email: email.trim(),
        password,
        role,
        schoolName: schoolName.trim(),
        schoolType,
        schoolLevel: schoolType,
        boardingMode,
        subscriptionPlan,
    });

    const handleSubmit = (event) => {
        event.preventDefault();

        const nameErr = !adminName.trim();
        const schoolErr = !schoolName.trim();
        const typeErr = !schoolType;
        const emailErr = !email.trim();
        const passMsg = validateNewPassword(password);
        const passErr = Boolean(passMsg);

        setAdminNameError(nameErr);
        setSchoolNameError(schoolErr);
        setSchoolTypeError(typeErr);
        setEmailError(emailErr);
        setPasswordError(passErr);
        setPasswordErrorText(passMsg || "");

        if (nameErr || schoolErr || typeErr || emailErr || passErr) return;

        const fields = buildRegistrationFields();
        setLoader(false);
        setExistingAccountError("");
        setPendingRegistration(fields);
        setShowRegistrationPayment(true);
    };

    const finalizeRegistration = async ({ persistSession = true } = {}) => {
        if (!pendingRegistration) {
            throw new Error("Registration details are missing. Go back and fill in the form again.");
        }
        try {
            return await createAdminAccount(pendingRegistration, dispatch, { persistSession });
        } catch (err) {
            if (err?.isAvailabilityError || isAvailabilityMessage(err?.message)) {
                setShowRegistrationPayment(false);
                setExistingAccountError(err.message);
                clearRegistrationCheckout();
            }
            throw err;
        }
    };

    const goToPendingVerification = () => {
        navigate("/Admin/pending-verification");
    };

    const handleContinueTrial = async () => {
        clearRegistrationCheckout();
        const account = await finalizeRegistration();
        try {
            await notifySystemAdminOfSchoolRegistration({
                ...pendingRegistration,
                subscriptionPlan: account?.subscriptionPlan || pendingRegistration?.subscriptionPlan,
            });
        } catch (err) {
            console.warn("Could not send verification email to system admin:", err);
        }
        setShowRegistrationPayment(false);
        setPendingRegistration(null);
        setMessage(FEEDBACK.schoolRegisteredPendingVerification);
        setPopupSeverity("success");
        setShowPopup(true);
        setTimeout(goToPendingVerification, SUCCESS_NAVIGATE_DELAY_MS);
    };

    const handlePaymentComplete = async () => {
        clearRegistrationCheckout();
        setResumeCheckout(null);
        try {
            await notifySystemAdminOfSchoolRegistration(pendingRegistration);
        } catch (err) {
            console.warn("Could not send verification email to system admin:", err);
        }
        setShowRegistrationPayment(false);
        setPendingRegistration(null);
        setMessage(FEEDBACK.schoolRegisteredPendingVerification);
        setPopupSeverity("success");
        setShowPopup(true);
        setTimeout(goToPendingVerification, SUCCESS_NAVIGATE_DELAY_MS);
    };

    const handleCloseActivation = () => {
        clearRegistrationCheckout();
        setResumeCheckout(null);
        setShowRegistrationPayment(false);
    };

    return (
        <>
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    bgcolor: "background.default",
                }}
            >
                {/* Brand panel */}
                <Box
                    sx={{
                        display: { xs: "none", md: "flex" },
                        width: { md: "42%", lg: "44%" },
                        flexDirection: "column",
                        justifyContent: "space-between",
                        p: { md: 4, lg: 6 },
                        color: "common.white",
                        bgcolor: "common.black",
                        background: (t) =>
                            t.palette.mode === "light"
                                ? "linear-gradient(165deg, #111111 0%, #333333 100%)"
                                : "linear-gradient(165deg, #0a0a0a 0%, #1a1a1a 100%)",
                    }}
                >
                    <Box>
                        <Button
                            component={RouterLink}
                            to="/"
                            startIcon={<ArrowBackIcon />}
                            sx={{
                                color: "inherit",
                                mb: 4,
                                "&:hover": { bgcolor: alpha("#fff", 0.08) },
                            }}
                        >
                            Back to home
                        </Button>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                            <SchoolOutlinedIcon sx={{ fontSize: 36 }} />
                            <Typography variant="h5" fontWeight={800}>
                                {APP_NAME}
                            </Typography>
                        </Stack>
                        <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.15, mb: 2 }}>
                            Register your school
                        </Typography>
                        <Typography sx={{ opacity: 0.9, lineHeight: 1.7, maxWidth: 400 }}>
                            Create a director account and set up your institution in minutes. You will manage
                            enrolment, academics, finance, and staff from one admin portal.
                        </Typography>
                    </Box>

                    <Stack spacing={1.25} sx={{ mt: 4 }}>
                        {BENEFITS.map((line) => (
                            <Stack key={line} direction="row" spacing={1} alignItems="flex-start">
                                <CheckCircleOutlineIcon sx={{ fontSize: 20, opacity: 0.9, mt: 0.1 }} />
                                <Typography variant="body2" sx={{ opacity: 0.92 }}>
                                    {line}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>

                    <Box
                        sx={{
                            mt: 4,
                            borderRadius: 2,
                            overflow: "hidden",
                            border: (t) => `1px solid ${alpha("#fff", 0.2)}`,
                            maxHeight: 160,
                        }}
                    >
                        <Box
                            component="img"
                            src={imgSchoolBuilding}
                            alt="School building"
                            sx={{ width: "100%", height: 160, objectFit: "cover", display: "block" }}
                        />
                    </Box>
                    <Typography variant="caption" sx={{ mt: 2, opacity: 0.7 }}>
                        Ministry-aligned · MANEB curriculum · MIE scheme format
                    </Typography>
                </Box>

                {/* Form panel */}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        py: { xs: 3, md: 4 },
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Container maxWidth="sm" disableGutters>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                            <ThemeToggle size="small" />
                        </Box>
                        <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
                            <Button
                                component={RouterLink}
                                to="/"
                                startIcon={<ArrowBackIcon />}
                                size="small"
                                sx={{ mb: 2 }}
                            >
                                Back to home
                            </Button>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <SchoolOutlinedIcon color="primary" />
                                <Typography variant="h6" fontWeight={800}>
                                    School registration
                                </Typography>
                            </Stack>
                        </Box>

                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 4 },
                                borderRadius: 3,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                boxShadow: (t) => `0 8px 40px ${alpha(t.palette.common.black, 0.06)}`,
                            }}
                        >
                            <Box sx={{ display: { xs: "none", md: "block" }, mb: 3 }}>
                                <Chip
                                    label="Director account"
                                    size="small"
                                    sx={{
                                        mb: 1.5,
                                        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                        color: "primary.main",
                                        fontWeight: 600,
                                    }}
                                />
                                <Typography variant="h4" fontWeight={800} gutterBottom>
                                    Create your school
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    You will be the school director (admin) with full access to manage the system.
                                </Typography>
                            </Box>

                            <Box component="form" noValidate onSubmit={handleSubmit}>
                                <Stack spacing={2}>
                                    {existingAccountError ? (
                                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                            {existingAccountError.includes("Email")
                                                ? "An account with this email already exists. You likely finished the first step of registration earlier (before payment or free trial)."
                                                : "A school with this name is already registered. Use the director email and password from that registration."}
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                Sign in with the same email and password you used before. Your school
                                                still has a {TRIAL_DAYS}-day free trial unless you already used it.
                                            </Typography>
                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
                                                <Button
                                                    component={RouterLink}
                                                    to={`/login?email=${encodeURIComponent(email.trim())}`}
                                                    variant="contained"
                                                    size="small"
                                                >
                                                    Sign in
                                                </Button>
                                                <Button
                                                    component={RouterLink}
                                                    to="/login"
                                                    variant="outlined"
                                                    size="small"
                                                >
                                                    Forgot which email?
                                                </Button>
                                            </Stack>
                                        </Alert>
                                    ) : null}

                                    <TextField
                                        required
                                        fullWidth
                                        id="adminName"
                                        label="Your full name"
                                        name="adminName"
                                        autoComplete="name"
                                        autoFocus
                                        value={adminName}
                                        onChange={(e) => {
                                            setAdminName(e.target.value);
                                            setAdminNameError(false);
                                        }}
                                        error={adminNameError}
                                        helperText={adminNameError ? "Name is required" : " "}
                                    />
                                    <TextField
                                        required
                                        fullWidth
                                        id="schoolName"
                                        label="School name"
                                        name="schoolName"
                                        placeholder="e.g. Lilongwe Secondary School"
                                        value={schoolName}
                                        onChange={(e) => {
                                            setSchoolName(e.target.value);
                                            setSchoolNameError(false);
                                        }}
                                        error={schoolNameError}
                                        helperText={schoolNameError ? "School name is required" : " "}
                                    />
                                    <TextField
                                        required
                                        fullWidth
                                        select
                                        id="schoolType"
                                        label="School type"
                                        name="schoolType"
                                        value={schoolType}
                                        onChange={(e) => {
                                            setSchoolType(e.target.value);
                                            setSchoolTypeError(false);
                                        }}
                                        error={schoolTypeError}
                                        helperText={
                                            schoolTypeError
                                                ? "School type is required"
                                                : "Determines class levels and term structure"
                                        }
                                    >
                                        <MenuItem value="" disabled>
                                            Select school type
                                        </MenuItem>
                                        {SCHOOL_TYPES.map((t) => (
                                            <MenuItem key={t.value} value={t.value}>
                                                {t.label} — {t.hint}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        required
                                        fullWidth
                                        select
                                        id="boardingMode"
                                        label="Residence offering"
                                        name="boardingMode"
                                        value={boardingMode}
                                        onChange={(e) => setBoardingMode(e.target.value)}
                                        helperText="Day only, boarding only, or both — enables hostel module when boarding applies"
                                    >
                                        {BOARDING_MODES.map((m) => (
                                            <MenuItem key={m.value} value={m.value}>
                                                {m.label} — {m.hint}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        required
                                        fullWidth
                                        id="email"
                                        label="Email address"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setEmailError(false);
                                        }}
                                        error={emailError}
                                        helperText={emailError ? "Email is required" : " "}
                                    />
                                    <TextField
                                        required
                                        fullWidth
                                        name="password"
                                        label="Password"
                                        type={toggle ? "text" : "password"}
                                        id="password"
                                        autoComplete="new-password"
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            setPasswordError(false);
                                            setPasswordErrorText("");
                                        }}
                                        error={passwordError}
                                        helperText={
                                            passwordError
                                                ? passwordErrorText || "Password is required"
                                                : PASSWORD_MIN_HELPER
                                        }
                                        inputProps={{ minLength: 8 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setToggle((v) => !v)}
                                                        edge="end"
                                                        aria-label="toggle password"
                                                    >
                                                        {toggle ? (
                                                            <VisibilityOffOutlinedIcon />
                                                        ) : (
                                                            <VisibilityOutlinedIcon />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                            Choose your plan
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                            One {TRIAL_DAYS}-day free trial per school. Student limits apply to your
                                            whole school register.
                                        </Typography>
                                        <FormControl component="fieldset" fullWidth>
                                            <RadioGroup
                                                value={subscriptionPlan}
                                                onChange={(e) => setSubscriptionPlan(e.target.value)}
                                            >
                                                {SUBSCRIPTION_PLANS.map((plan) => (
                                                    <Box
                                                        key={plan.id}
                                                        sx={{
                                                            mb: 1,
                                                            p: 1.5,
                                                            borderRadius: 2,
                                                            border: (t) =>
                                                                subscriptionPlan === plan.id
                                                                    ? `2px solid ${t.palette.primary.main}`
                                                                    : `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                                            bgcolor: (t) =>
                                                                subscriptionPlan === plan.id
                                                                    ? alpha(t.palette.primary.main, 0.06)
                                                                    : "transparent",
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="flex-start">
                                                            <Radio value={plan.id} sx={{ mt: -0.5 }} />
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography fontWeight={700}>
                                                                    {plan.name} — {formatPriceMwk(plan.priceMwk)}{" "}
                                                                    <Typography
                                                                        component="span"
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        fontWeight={400}
                                                                    >
                                                                        ({plan.billingLabel})
                                                                    </Typography>
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {plan.studentRange}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </RadioGroup>
                                            <FormHelperText>
                                                Includes: {PLAN_INCLUDES.join(" · ")}
                                            </FormHelperText>
                                        </FormControl>
                                    </Box>
                                </Stack>

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={loader}
                                    sx={{ mt: 3, py: 1.35, fontWeight: 700 }}
                                >
                                    {loader ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        "Continue to activation"
                                    )}
                                </Button>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    align="center"
                                    sx={{ mt: 2.5 }}
                                >
                                    Already registered?{" "}
                                    <Link
                                        component={RouterLink}
                                        to="/login"
                                        fontWeight={700}
                                        underline="hover"
                                    >
                                        Sign in as director
                                    </Link>
                                </Typography>
                            </Box>
                        </Paper>
                    </Container>
                </Box>
            </Box>
            <RegistrationPaymentChoiceModal
                open={showRegistrationPayment}
                pendingRegistration={pendingRegistration}
                planId={subscriptionPlan}
                initialResume={resumeCheckout}
                onClose={handleCloseActivation}
                onContinueTrial={handleContinueTrial}
                onCreateAccount={finalizeRegistration}
                onPaymentComplete={handlePaymentComplete}
            />
            <Popup
                message={message}
                setShowPopup={setShowPopup}
                showPopup={showPopup}
                severity={popupSeverity}
            />
        </>
    );
};

export default AdminRegisterPage;
